import { useRef, useState } from 'react';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';

import act from '@flux/actions/actView';
import HierarchyDataGrid from '../ItemRelations/HierarchyDataGrid';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { CElement, CEntity, IForm } from '@ts/ui-models';
import { Button, Grid, Select, Typography, Icon } from '@ts/storybook';
import { useAppDispatch } from '@redux/store';
import { updateData } from '@redux/slices/functionals/request/requestActions';

interface IHierarchyTabProps extends IForm {
  onlyParent?: boolean;
  onlyChild?: boolean;
}
const HierarchyTab = (props: IHierarchyTabProps) => {
  const dispatch = useAppDispatch();
  const dataGridRefParent = useRef<any>(null);
  const dataGridRefChild = useRef<any>(null);
  const isViewMode = props.mode == 'view';

  const [state, setState] = useState<{
    sourceItem: CElement;
    memberType: 'parent' | 'child' | undefined;
    targetItem: CElement | null;
    relation: CElement | null;
    showActions: boolean;
  }>({
    sourceItem: new CElement(props.values),
    memberType: undefined, // parent or child
    targetItem: null,
    relation: null,
    showActions: props.onlyChild || props.onlyParent || props.mode != 'edit' || false,
  });

  //#region Functions
  const getAuxPaths = memberType => {
    let keyName;
    let elementKey;
    if (memberType === 'parent') {
      keyName = 'parents'; //'parents'
      elementKey = 'pe';
    } else if (memberType === 'child') {
      keyName = 'children'; //children
      elementKey = 'ce';
    }
    const { sourceItem } = state;
    const id = sourceItem.getID();
    const updateUrl = `/api/v2/item/${id}`;

    return { keyName, elementKey, updateUrl };
  };
  const shouldDisableAddButton = !state.memberType || !state.targetItem || !state.relation || !state.sourceItem;
  //#endregion

  //#region Handlers
  const handleClickRemoveMember = async (memberType: 'parent' | 'child', xElementToRemove) => {
    const { keyName, elementKey, updateUrl } = getAuxPaths(memberType);
    const memberToRemove = {
      elementId: xElementToRemove[elementKey].logicalId,
      elementRelationLid: xElementToRemove.relation.logicalId,
      elementField: 'logicalId',
    };
    const attrs = {};
    attrs[keyName] = { remove: [memberToRemove] };

    await dispatch(
      updateData({
        url: updateUrl,
        dataBody: attrs,
      })
    );

    memberType === 'parent' ? dataGridRefParent.current?.refreshDataGrid() : dataGridRefChild.current?.refreshDataGrid();
  };

  const handleClickAddMember = async () => {
    const { targetItem, memberType, relation } = state;

    const { keyName, updateUrl } = getAuxPaths(memberType);

    const newMember = {
      elementId: targetItem?.getID(),
      elementRelationLid: relation?.getID(),
      elementRelationTypeLid: relation?.elementRelationTypeLid,
      elementField: 'logicalId',
    };
    const attrs = {};
    attrs[keyName] = { add: [newMember] };

    await dispatch(
      updateData({
        url: updateUrl,
        dataBody: attrs,
      })
    );

    setState({
      ...state,
      targetItem: null,
    });

    memberType === 'parent' ? dataGridRefParent.current?.refreshDataGrid() : dataGridRefChild.current?.refreshDataGrid();
  };

  //#endregion

  //#region Render
  const renderParents = () => {
    if (!props.values) return;

    return (
      <HierarchyDataGrid refGrid={dataGridRefParent} entity={props.values} onClickRemove={handleClickRemoveMember} isParent />
    );
  };

  const renderChildren = () => {
    if (!props.values) return;

    return (
      <HierarchyDataGrid refGrid={dataGridRefChild} entity={props.values} onClickRemove={handleClickRemoveMember} isChildren />
    );
  };

  const renderFormLinkMember = () => {
    return (
      <Grid container spacing={1} size={12}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" text="Add members to the hierarchy" />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <Select
            label="Type of member"
            value={state.memberType}
            onChange={value => setState({ ...state, memberType: value as 'parent' | 'child', targetItem: null, relation: null })}
            disabled={props.mode != 'edit'}
            options={[
              { label: 'Parent', value: 'parent' },
              { label: 'Child', value: 'child' },
            ]}
          />
        </Grid>

        {state.memberType && (
          <Grid size={{ xs: 3 }}>
            <WrapperEntitySelector
              searchComponent={generateSearchEntitySelector('itemRelation')}
              value={state.relation}
              onChangeEntity={entity => setState({ ...state, relation: entity as any, targetItem: null })}
              label={'Relation'}
              isReadOnly={isViewMode || !state.memberType}
            />
          </Grid>
        )}

        {state.relation && (
          <Grid size={{ xs: 3 }}>
            <WrapperEntitySelector
              label="Item to add"
              value={state.targetItem}
              onChangeEntity={entity => setState({ ...state, targetItem: entity as any })}
              searchComponent={generateSearchEntitySelector('item')}
              disabled={isViewMode || !state.memberType || !state.relation}
            />
          </Grid>
        )}

        {state.targetItem && state.targetItem && (
          <Grid size={{ xs: 3 }}>
            <Button
              variant="outlined"
              onClick={handleClickAddMember}
              disabled={shouldDisableAddButton}
              label="Add"
              fullWidth
              startIcon={<Icon icon="link" />}
            />
          </Grid>
        )}
      </Grid>
    );
  };
  //#endregion

  if (props.onlyParent) return renderParents();
  if (props.onlyChild) return renderChildren();

  return (
    <Grid id="hierarchy-tab" container direction={'column'} spacing={4} pb={2}>
      {!isViewMode && renderFormLinkMember()}
      {renderParents()}
      {renderChildren()}
    </Grid>
  );
};

export default HierarchyTab;
